import React, { useState, useEffect, useCallback } from 'react';
import Modal from '../ui/Modal';
import DncConfirmModal from '../ui/DncConfirmModal';
import { Button, Select } from '../ui/Kit';
import RoomTypeSelector from './RoomTypeSelector';

const OUTCOME_OPTIONS = [
  { value: 'answered', label: 'Answered' },
  { value: 'voicemail', label: 'Voicemail' },
  { value: 'no_answer', label: 'No Answer' },
  { value: 'busy', label: 'Busy' },
  { value: 'callback_requested', label: 'Callback Requested' },
  { value: 'booked_meeting', label: 'Booked Meeting' },
  { value: 'closed_business', label: 'Closed Business' },
];

const LEAD_STATUS_OPTIONS = [
  { value: 'new', label: 'New' },
  { value: 'contacted', label: 'Contacted' },
  { value: 'follow_up', label: 'Follow-Up' },
  { value: 'appointment_set', label: 'Appointment Set' },
  { value: 'closed_won', label: 'Closed Won' },
  { value: 'closed_lost', label: 'Closed Lost' },
  { value: 'dnc', label: 'DNC' },
];

function formatDuration(seconds) {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  if (m === 0) return `${s}s`;
  return `${m}m ${s}s`;
}

/**
 * Reusable post-call logging modal. Works in both the main Softphone dialer
 * and the workspace DialerBar.
 *
 * @param {Object} props
 * @param {boolean} props.isOpen
 * @param {function} props.onSkip        - close without saving
 * @param {function} props.onSave        - ({outcome, leadStatus, notes}) => Promise
 * @param {string}  props.leadName
 * @param {string}  props.currentLeadStatus - pre-fill the lead status dropdown
 * @param {string}  [props.suggestedOutcome='answered']
 * @param {string}  [props.defaultNotes='']
 * @param {number}  [props.durationSeconds=0]
 * @param {boolean} [props.saving=false]- controlled externally so parent can show loading
 */
export default function PostCallModal({
  isOpen,
  onSkip,
  onSave,
  leadName = 'Lead',
  currentLeadStatus = 'new',
  suggestedOutcome = 'answered',
  defaultNotes = '',
  durationSeconds = 0,
  saving = false,
}) {
  const [outcome, setOutcome] = useState(suggestedOutcome);
  const [leadStatus, setLeadStatus] = useState(currentLeadStatus);
  const [notes, setNotes] = useState(defaultNotes);
  const [roomType, setRoomType] = useState(null);
  const [dncOpen, setDncOpen] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setOutcome(suggestedOutcome);
      setLeadStatus(currentLeadStatus);
      setNotes(defaultNotes);
      setRoomType(null);
      setDncOpen(false);
    }
  }, [isOpen, suggestedOutcome, currentLeadStatus, defaultNotes]);

  const handleSaveClick = useCallback(() => {
    if (!outcome) return;
    if (leadStatus === 'dnc') {
      setDncOpen(true);
      return;
    }
    onSave?.({ outcome, leadStatus, notes, roomType });
  }, [outcome, leadStatus, notes, roomType, onSave]);

  const handleDncConfirm = useCallback(() => {
    setDncOpen(false);
    onSave?.({ outcome, leadStatus, notes, roomType });
  }, [outcome, leadStatus, notes, roomType, onSave]);

  return (
    <>
      <Modal isOpen={isOpen} onClose={onSkip} title="Log This Call" className="max-w-lg">
        <p className="text-sm text-gray-500 mb-4">
          <span className="font-medium text-gray-700">{leadName}</span>
          {durationSeconds > 0 && (
            <span className="ml-2 text-xs bg-gray-100 text-gray-600 rounded-full px-2 py-0.5 font-mono">
              {formatDuration(durationSeconds)}
            </span>
          )}
        </p>

        <div className="space-y-4">
          <Select
            label="Call Outcome *"
            options={OUTCOME_OPTIONS}
            value={outcome}
            onChange={(e) => setOutcome(e.target.value)}
            disabled={saving}
          />

          <RoomTypeSelector value={roomType} onChange={setRoomType} disabled={saving} />

          <div>
            <Select
              label="Update Lead Status"
              options={LEAD_STATUS_OPTIONS}
              value={leadStatus}
              onChange={(e) => setLeadStatus(e.target.value)}
              disabled={saving}
            />
            <p className="text-[11px] text-gray-500 mt-1">
              Current: {String(currentLeadStatus).replace(/_/g, ' ')}
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
            <textarea
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent min-h-[96px] resize-y"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Add notes about this call…"
              disabled={saving}
            />
          </div>

          <div className="flex gap-3 pt-2">
            <Button
              variant="outline"
              className="flex-1"
              onClick={onSkip}
              disabled={saving}
            >
              Skip
            </Button>
            <Button
              className="flex-1"
              onClick={handleSaveClick}
              disabled={saving || !outcome}
              isLoading={saving}
            >
              Save
            </Button>
          </div>
        </div>
      </Modal>

      <DncConfirmModal
        isOpen={dncOpen}
        onCancel={() => setDncOpen(false)}
        onConfirm={handleDncConfirm}
        leadName={leadName}
      />
    </>
  );
}
